import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation } from '#router'
import { SITE_NAME_SHORT, SITE_NAME_TAB } from '@/lib/seo-config'

interface TitleContextValue {
  register: (id: string, title: string | null) => void
}

const TitleContext = createContext<TitleContextValue | null>(null)
let titleRegistrationCounter = 0

function editorSlugToTitle(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/**
 * Sole writer of `document.title`. Consumers register their title via
 * `usePageTitle` keyed by a stable id; the most-recently-registered entry
 * wins. Cleanup removes only the consumer's own entry, so transition windows
 * where two pages are briefly mounted (Suspense / browser back) can't race —
 * the displayed title is derived from the registry, not from a single shared
 * setter that the last effect to fire happens to win.
 */
export function TitleProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const { page } = location
  const locationKey = `${location.pathname}${location.search}`
  const [titles, setTitles] = useState<Map<string, string>>(() => new Map())

  const register = useCallback((id: string, title: string | null) => {
    setTitles((prev) => {
      const next = new Map(prev)
      if (title) {
        // Delete-then-set so updates to an existing id move to the end of
        // iteration order; `Map.set` alone preserves original insertion
        // position, which would break the "most-recently-registered wins" rule.
        next.delete(id)
        next.set(id, title)
      } else {
        next.delete(id)
      }
      return next
    })
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: locationKey intentionally retriggers URL-derived title updates when window.location changes.
  useLayoutEffect(() => {
    const rawPathname =
      typeof window !== 'undefined' ? window.location.pathname : ''
    const normalizedPath =
      rawPathname.length > 1 ? rawPathname.replace(/\/+$/, '') : rawPathname
    const isHomePath = normalizedPath === '/'
    const isProblemsPath = normalizedPath === '/problems'
    const isEditorPath = normalizedPath.startsWith('/editor')
    const pageTitle = Array.from(titles.values()).at(-1) ?? null
    const editorSlug =
      typeof window !== 'undefined' && isEditorPath
        ? new URLSearchParams(window.location.search).get('problem')
        : null
    let nextTitle: string
    if (editorSlug) {
      // Editor title must be deterministic from URL to avoid any race with
      // async data loading or transient title registrations.
      nextTitle = `${editorSlugToTitle(editorSlug)} - ${SITE_NAME_SHORT}`
    } else if (isHomePath || isProblemsPath) {
      // Explicitly force default title on home/problems so a stale editor
      // title cannot persist when navigating via the header logo.
      nextTitle = SITE_NAME_TAB
    } else if (pageTitle) {
      nextTitle = `${pageTitle} - ${SITE_NAME_SHORT}`
    } else if (page?.title) {
      nextTitle = `${page.title} - ${SITE_NAME_TAB}`
    } else {
      nextTitle = SITE_NAME_TAB
    }
    document.title = nextTitle
  }, [titles, page, locationKey])

  // biome-ignore lint/correctness/useExhaustiveDependencies: locationKey intentionally replaces the URL-derived editor-title observer after navigation.
  useEffect(() => {
    const rawPathname =
      typeof window !== 'undefined' ? window.location.pathname : ''
    const normalizedPath =
      rawPathname.length > 1 ? rawPathname.replace(/\/+$/, '') : rawPathname
    const isEditorPath = normalizedPath.startsWith('/editor')
    const editorSlug =
      typeof window !== 'undefined' && isEditorPath
        ? new URLSearchParams(window.location.search).get('problem')
        : null
    if (!editorSlug) return

    const expectedTitle = `${editorSlugToTitle(editorSlug)} - ${SITE_NAME_SHORT}`

    const titleElement = document.querySelector('title')
    if (!titleElement) return

    const observer = new MutationObserver(() => {
      if (document.title !== expectedTitle) {
        document.title = expectedTitle
      }
    })

    observer.observe(titleElement, {
      childList: true,
      characterData: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [locationKey])

  const value = useMemo(() => ({ register }), [register])

  return <TitleContext value={value}>{children}</TitleContext>
}

/**
 * Registers a dynamic page title with the provider. Each call gets its own id,
 * so cleanup removes only this consumer's entry — it can't clobber another
 * consumer's value if both are mounted briefly during a transition.
 */
export function usePageTitle(title: string | null | undefined) {
  const ctx = use(TitleContext)
  const idRef = useRef<string | null>(null)
  if (!idRef.current) {
    titleRegistrationCounter += 1
    idRef.current = `title-${titleRegistrationCounter}`
  }

  useEffect(() => {
    if (!ctx) return
    const id = idRef.current!
    ctx.register(id, title ?? null)
    return () => {
      ctx.register(id, null)
    }
  }, [title, ctx])
}
